/**
 * Inventory Database Backup Service
 * 
 * Syncs the inventory database (Firebase) to Google Sheets as a backup.
 * Spreadsheet: https://docs.google.com/spreadsheets/d/1i6kflRpoKqAxLxsofigxdDQhPxbyci3HkDU1otWnwIY
 * 
 * Tabs:
 * - IDALL: All inventory items
 * - IDDRAFTS: Draft items only
 * - IDWATCHES: Watch items only
 * - IDSOLD: Sold items only
 */

import { InventoryDatabaseItem, InventoryStatus } from '../types/inventory';

// Backup spreadsheet ID - the FIN backup sheet
const BACKUP_SPREADSHEET_ID = '1i6kflRpoKqAxLxsofigxdDQhPxbyci3HkDU1otWnwIY';

// Google Apps Script Web App URL for backup operations
// Deployed and running: https://script.google.com/macros/s/AKfycbwfma52Mb6GDGHqXgN2hb7k1ekt9yg2B-WHrjl3xGcQYIrhdOzUBBV1Vom0fkioYQ59MA/exec
const BACKUP_APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwfma52Mb6GDGHqXgN2hb7k1ekt9yg2B-WHrjl3xGcQYIrhdOzUBBV1Vom0fkioYQ59MA/exec';

// Tab names for different statuses
const STATUS_TO_TAB: Record<InventoryStatus, string> = {
  draft: 'IDDRAFTS',
  jewelry: 'IDALL',  // Jewelry goes to IDALL (main items)
  watch: 'IDWATCHES',
  sold: 'IDSOLD'
};

// Column headers for the backup sheet (in order)
const COLUMN_HEADERS = [
  'ID',           // A - Firebase document ID
  'SKU',          // B
  'Barcode',      // C
  'Date',         // D
  'Age',          // E
  'Location',     // F
  'Type',         // G
  'Metal',        // H
  'Weight (g)',   // I
  'New Melt',     // J
  'Stones',       // K
  'DPrem/WWS',    // L
  'Retail',       // M
  'Cost',         // N
  'Wholesale',    // O
  'Price',        // P
  'Margin %',     // Q
  'Day 0',        // R
  'Ebay 0',       // S
  'Deny',         // T
  'Title',        // U
  'Links',        // V
  'Notes',        // W
  'Flag T',       // X
  'Flag P',       // Y
  'Flag L',       // Z
  'Flag E',       // AA
  'Flag S',       // AB
  'Status',       // AC
  'Created At',   // AD
  'Updated At'    // AE
];

/**
 * Convert inventory item to row array for Google Sheets
 */
function itemToRow(item: InventoryDatabaseItem): string[] {
  const formatDate = (date: Date | undefined): string => {
    if (!date) return '';
    try {
      return date instanceof Date ? date.toISOString().split('T')[0] : '';
    } catch {
      return '';
    }
  };

  const formatDateTime = (date: Date | undefined): string => {
    if (!date) return '';
    try {
      return date instanceof Date ? date.toISOString() : '';
    } catch {
      return '';
    }
  };

  return [
    item.id || '',
    item.sku || '',
    item.barcode || '',
    formatDate(item.date),
    String(item.age || 0),
    item.location || '',
    item.type || '',
    item.metal || '',
    String(item.weightGrams || 0),
    String(item.newMelt || 0),
    String(item.stones || 0),
    String(item.dpremWws || 0),
    String(item.retail || 0),
    String(item.cost || 0),
    String(item.wholesale || 0),
    String(item.price || 0),
    String(item.margin || 0),
    String(item.day0 || 0),
    String(item.ebay0 || 0),
    String(item.deny || 0),
    item.title || '',
    item.links || '',
    item.notes || '',
    item.flagT ? 'TRUE' : 'FALSE',
    item.flagP ? 'TRUE' : 'FALSE',
    item.flagL ? 'TRUE' : 'FALSE',
    item.flagE ? 'TRUE' : 'FALSE',
    item.flagS ? 'TRUE' : 'FALSE',
    item.status || '',
    formatDateTime(item.createdAt),
    formatDateTime(item.updatedAt)
  ];
}

/**
 * Get the target tabs for an item based on its status
 * Returns ['IDALL'] for jewelry, plus the status-specific tab
 */
function getTargetTabs(status: InventoryStatus): string[] {
  const tabs: string[] = [];
  
  // Status-specific tab
  const statusTab = STATUS_TO_TAB[status];
  if (statusTab) {
    tabs.push(statusTab);
  }
  
  return tabs;
}

class InventoryDatabaseBackupService {
  private isEnabled: boolean = true;
  private syncQueue: Array<() => Promise<void>> = [];
  private isProcessingQueue: boolean = false;

  /**
   * Enable/disable backup sync
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log(`📋 Inventory backup sync ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Sync via Google Apps Script
   */
  private async syncViaAppsScript(action: string, data: any): Promise<{ success: boolean; error?: string; data?: any }> {
    try {
      console.log(`📤 [Backup] Syncing via Apps Script: ${action}`, data);

      const params = new URLSearchParams();
      params.append('action', action);
      params.append('data', JSON.stringify({
        ...data,
        spreadsheetId: BACKUP_SPREADSHEET_ID
      }));

      const response = await fetch(BACKUP_APPS_SCRIPT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseText = await response.text();
      console.log('📄 [Backup] Raw response:', responseText);

      try {
        const result = JSON.parse(responseText);
        return result;
      } catch {
        // If response isn't JSON but request succeeded, assume success
        return { success: true };
      }
    } catch (error: any) {
      console.error('❌ [Backup] Apps Script sync failed:', error);
      return { success: false, error: error?.message || 'Unknown error' };
    }
  }

  /**
   * Add an item to the backup sheet
   */
  async addItem(item: InventoryDatabaseItem): Promise<void> {
    if (!this.isEnabled) return;

    try {
      console.log(`📋 [Backup] Adding item ${item.sku} to backup sheet`);

      const targetTabs = getTargetTabs(item.status);
      const rowData = itemToRow(item);

      for (const tabName of targetTabs) {
        const result = await this.syncViaAppsScript('backupAddRow', {
          tabName,
          rowData,
          headers: COLUMN_HEADERS
        });

        if (result.success) {
          console.log(`✅ [Backup] Added ${item.sku} to ${tabName}`);
        } else {
          console.error(`❌ [Backup] Failed to add ${item.sku} to ${tabName}:`, result.error);
        }
      }
    } catch (error) {
      console.error(`❌ [Backup] Error adding item ${item.sku}:`, error);
    }
  }

  /**
   * Update an item in the backup sheet
   */
  async updateItem(item: InventoryDatabaseItem, previousStatus?: InventoryStatus): Promise<void> {
    if (!this.isEnabled) return;

    try {
      console.log(`📋 [Backup] Updating item ${item.sku} in backup sheet`);

      const rowData = itemToRow(item);
      const currentTabs = getTargetTabs(item.status);

      // If status changed, we need to remove from old tab and add to new
      if (previousStatus && previousStatus !== item.status) {
        const oldTabs = getTargetTabs(previousStatus);
        
        // Remove from tabs that are no longer relevant
        for (const oldTab of oldTabs) {
          if (!currentTabs.includes(oldTab)) {
            await this.syncViaAppsScript('backupDeleteRow', {
              tabName: oldTab,
              id: item.id
            });
            console.log(`🗑️ [Backup] Removed ${item.sku} from ${oldTab} (status changed)`);
          }
        }
      }

      // Update or add to current tabs
      for (const tabName of currentTabs) {
        const result = await this.syncViaAppsScript('backupUpdateRow', {
          tabName,
          id: item.id,
          rowData,
          headers: COLUMN_HEADERS
        });

        if (result.success) {
          console.log(`✅ [Backup] Updated ${item.sku} in ${tabName}`);
        } else {
          // If update failed because row doesn't exist, add it
          console.log(`⚠️ [Backup] Update failed for ${item.sku} in ${tabName}, trying add...`);
          await this.syncViaAppsScript('backupAddRow', {
            tabName,
            rowData,
            headers: COLUMN_HEADERS
          });
        }
      }
    } catch (error) {
      console.error(`❌ [Backup] Error updating item ${item.sku}:`, error);
    }
  }

  /**
   * Delete an item from the backup sheet
   */
  async deleteItem(id: string, status: InventoryStatus): Promise<void> {
    if (!this.isEnabled) return;

    try {
      console.log(`📋 [Backup] Deleting item ${id} from backup sheet`);

      const targetTabs = getTargetTabs(status);

      for (const tabName of targetTabs) {
        const result = await this.syncViaAppsScript('backupDeleteRow', {
          tabName,
          id
        });

        if (result.success) {
          console.log(`✅ [Backup] Deleted ${id} from ${tabName}`);
        } else {
          console.error(`❌ [Backup] Failed to delete ${id} from ${tabName}:`, result.error);
        }
      }
    } catch (error) {
      console.error(`❌ [Backup] Error deleting item ${id}:`, error);
    }
  }

  /**
   * Full sync - sync all items from Firebase to the backup sheet
   * This replaces all data in the sheet
   */
  async fullSync(items: InventoryDatabaseItem[]): Promise<{ success: boolean; counts: Record<string, number> }> {
    if (!this.isEnabled) {
      return { success: false, counts: {} };
    }

    try {
      console.log(`📋 [Backup] Starting full sync of ${items.length} items`);

      // Group items by status
      const draftItems = items.filter(i => i.status === 'draft');
      const jewelryItems = items.filter(i => i.status === 'jewelry');
      const watchItems = items.filter(i => i.status === 'watch');
      const soldItems = items.filter(i => i.status === 'sold');

      const counts: Record<string, number> = {
        IDDRAFTS: draftItems.length,
        IDALL: jewelryItems.length,
        IDWATCHES: watchItems.length,
        IDSOLD: soldItems.length
      };

      // Sync each tab
      const syncTab = async (tabName: string, tabItems: InventoryDatabaseItem[]) => {
        const rows = tabItems.map(item => itemToRow(item));
        
        const result = await this.syncViaAppsScript('backupFullSync', {
          tabName,
          headers: COLUMN_HEADERS,
          rows
        });

        if (result.success) {
          console.log(`✅ [Backup] Full sync completed for ${tabName}: ${tabItems.length} items`);
        } else {
          console.error(`❌ [Backup] Full sync failed for ${tabName}:`, result.error);
        }

        return result.success;
      };

      // Sync all tabs
      await Promise.all([
        syncTab('IDDRAFTS', draftItems),
        syncTab('IDALL', jewelryItems),
        syncTab('IDWATCHES', watchItems),
        syncTab('IDSOLD', soldItems)
      ]);

      console.log(`✅ [Backup] Full sync completed:`, counts);
      return { success: true, counts };

    } catch (error) {
      console.error('❌ [Backup] Full sync error:', error);
      return { success: false, counts: {} };
    }
  }

  /**
   * Queue an operation for async processing
   * This prevents blocking the UI during sync operations
   */
  queueOperation(operation: () => Promise<void>): void {
    this.syncQueue.push(operation);
    this.processQueue();
  }

  /**
   * Process the sync queue
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.syncQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.syncQueue.length > 0) {
      const operation = this.syncQueue.shift();
      if (operation) {
        try {
          await operation();
        } catch (error) {
          console.error('❌ [Backup] Queue operation failed:', error);
        }
      }
    }

    this.isProcessingQueue = false;
  }
}

// Export singleton instance
export const inventoryDatabaseBackupService = new InventoryDatabaseBackupService();

// Export class for testing
export { InventoryDatabaseBackupService };
